import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { StudioLayout } from '@/components/layouts/StudioLayout';
import { FloatingChatButton } from '@/components/molecules/FloatingChatButton';
import { LayoutProps } from '@/types';
import { flattenRoutes, routeTree } from '@/routes/config';

export interface AppLayoutProps extends LayoutProps {
  noPadding?: boolean;
}

// Computed once at module level — no runtime cost per render.
const ALL_ROUTES = flattenRoutes(routeTree);

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  className,
}) => {
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith('/auth');

  // Check if this is OAuth callback (root path with secret_key)
  const searchParams = new URLSearchParams(location.search);
  const isOAuthCallback = location.pathname === '/' && searchParams.has('secret_key');

  if (isAuthPage || isOAuthCallback) {
    return (
      <AuthLayout className={className}>
        {children}
      </AuthLayout>
    );
  }

  // Find the route whose path pattern matches the current URL, then read its
  // hideFloatingChat flag. Routes self-declare in config.ts — AppLayout never
  // needs to be edited when a new route is added.
  const matchedRoute = ALL_ROUTES.find(r => matchPath(r.path, location.pathname));
  const showFloatingChat = !matchedRoute?.meta?.hideFloatingChat;

  return (
    <>
      <StudioLayout>{children}</StudioLayout>
      {showFloatingChat && <FloatingChatButton />}
    </>
  );
};
